// @flow
import React from 'react';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import branch from 'recompose/branch';
import renderNothing from 'recompose/renderNothing';
import both from 'ramda/es/both';
import path from 'ramda/es/path';
import prop from 'ramda/es/prop';
import complement from 'ramda/es/complement';
import { useTranslation } from 'react-i18next';

import {
  isSmsSubscribeActive,
  isMonthly,
} from 'app/components/Tariff/reasons';

import withToggle from 'app/common/containers/withToggle';
import Periods from 'app/components/Tariff/Periods';
import Indicator from 'app/components/Subs/Indicator';
import Note from 'app/components/Tariff/Note';
import BoolItem from 'app/components/_ui/BoolItem';
import type { TariffType, Sub } from 'app/components/Tariff/Tariff';

import createOptionSubSelector from './selectOptionSub';
import styles from './SubTariff.scss';


/**
* Опция тарифа (отдельные настройки подписки пакетного тарифа, привязанные к определенной услуги)
* Например у тарифа на 2 услуги Столовая и Проходная, 2 опции подписок на смс для Столовой и Проходной
* Чтоб иметь опцию, тариф должен быть месячный (isMonthly) с включенной настройкой sms_subscribe_active
*/
type EProps = {
  tariff: TariffType,
  studentId: number,
  footnotes: Object,
  title: string,
}

const enhance = compose(
  branch(
    complement(both(isMonthly, isSmsSubscribeActive)),
    renderNothing
  ),
  // ищем подписку опции по id подписки с которой пришли
  // tariff_id такой подписки должен указывать на переданный тариф
  connect(
    () => {
      const selectOptionSub = createOptionSubSelector();
      return (state, props: EProps) => ({
        sub: selectOptionSub(state, props),
      });
    }
  ),
  withToggle()
);

type Props = {
  sub?: Sub,
  tariff: TariffType,
  footnotes: Object,
  title: string,
  handleToggle: (e: SyntheticEvent<HTMLElement>) => void,
  isOpen: boolean,
  studentId: number,
};

const SubTariff = ({
  sub,
  tariff,
  footnotes,
  title,
  handleToggle,
  isOpen,
  studentId,
}: Props) => {
  const paid = !!(sub && sub.sms_prepaid_stop_at);
  const isFree = !!(sub && sub.test_days_left);
  const { t } = useTranslation('tariff');

  return (
    <BoolItem className={styles.root} checked={paid} empty={!sub}>
      <span className={styles.title}>{title}</span>
      &nbsp;
      {sub && (paid
        ? <span className={styles.on}>
          {t('on')}
          &nbsp;
        </span>
        : <span className={styles.off}>
          {t('off')}
          &nbsp;
        </span>)}

      {/* не давать подписаться и посмотреть на что можно подписаться, когда подписка тестовая */}
      {!isFree
        && !prop('is_paused', sub)
        && <a
          className={styles.link}
          href="subscriptions"
          onClick={handleToggle}
        >
          {sub && !paid ? t('buy_subscribe') : t('available_subscriptions')}
        </a>}

      {!isFree
        && !prop('is_paused', sub)
        && <Note footnotes={footnotes} value="monthDailyPause" />}

      <div className={styles.row}>
        {isOpen && <div className={styles.col}>
          <Periods
            subId={sub && sub.id}
            paid={paid}
            periods={path(['month_daily_params', 'sms_periods'], tariff)}
            locked={!sub}
            isOpen={isOpen}
            externalToggle={handleToggle}
            studentId={studentId}
            tariffId={tariff.id}
            isSms
          />
        </div>}
        {paid && !isOpen
          && <div className={styles.col}>
            <Indicator
              subId={sub && sub.id}
              showSmsPrepayd
              showCircle
            />
          </div>}
      </div>
    </BoolItem>
  );
};

export default enhance(SubTariff);
